import React, { useState, useEffect, useCallback, useRef } from "react";

import { AppBar, Toolbar, ListItem, ListItemText, Drawer } from "@mui/material";
import Grid from '@mui/material/Grid2';
import MenuIcon from "@mui/icons-material/Menu"; // "@mui/icons-material/Menu";
import PersonIcon from "@mui/icons-material/Person"; // '@mui/icons-material/Person';

import { SourceBook } from "@dndModels";
import { DataType, SourceBookType, WorldSetting } from "@dndModels/Types";
import { DATA_TYPE_OPTIONS, FEAT_TIERS, WORLD_SETTINGS } from "@dndModels/Constants";
import { DisablableOption } from "@models";

import DandDAPI from "@dndUtilities/system_api";

import UserMenu from "@components/Navigation/UserMenu";
import Sidebar from "@components/Navigation/Sidebar";
import LoadingBox from "@components/input/LoadingBox";
import SelectSourceBookBox from "@dndComponents/select/SelectSourceBookBox";
import BugButton from "@components/input/buttons/BugButton";
import ModalOrDrawer from "@components/display/ModalOrDrawer";
import SelectStringBox from "@components/input/selects/SelectStringBox";
import StringBox from "@components/input/strings/StringBox";
import DeleteButton from "@components/input/buttons/DeleteButton";
import AddButton from "@components/input/buttons/AddButton";
import StringsBox from "@components/input/strings/StringsBox";
import { useAuth } from "react-oidc-context";

interface Props { 
  toggleLogin: () => void;
}

const api = DandDAPI.getInstance();

// const SPELL_FIELDS: DisablableOption[] = [
//   { id: "element", text: "Magic Element" },
//   { id: "tier", text: "Spell Tier"}
// ];

const FEAT_FIELDS: DisablableOption[] = [
  { id: "feat_tier", text: "Feat Type" },
  // { id: "repeatable", text: "Repeatable"}
];

const DEITY_FIELDS: DisablableOption[] = [
  { id: "pantheons", text: "Pantheon" },
  // { id: "repeatable", text: "Repeatable"}
];

export default function NavBar({
  toggleLogin
}: Props) {
  const auth = useAuth();
  const [fetched, setFetched] = useState(false);
  const [true_fetched, setTrueFetched] = useState(false);
  const [loading, setLoading] = useState(false);
  const [finished_loading, setFinishedLoading] = useState(false);
  const [ping, setPing] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [source_book_type, setSourceBookType] = useState<SourceBookType | "">(api.working_on_sb !== null ? api.working_on_sb.source_book_type : "");
  const [working_on_sb, setWorkingOnSB] = useState(api.working_on_sb);
  const [reloading, setReloading] = useState(false);
  const [wo_object_open, setWOObjectOpen] = useState(false);
  const mounted = useRef(false);

  useEffect(() => { 
    mounted.current = true;

    return () => {
      mounted.current = false;
    };
  }, []);

  const heightFinder: any = React.createRef();

  const updateHeight = useCallback(() => {
    if (heightFinder.current instanceof HTMLElement) {
      api.topHeight = heightFinder.current.clientHeight;
      // console.log(heightFinder.current.clientHeight);
      setReloading(true);
    }
  }, [heightFinder]);

  useEffect(() => {
    if (reloading) {
      setReloading(false);
    }
  }, [reloading, setReloading]);

  const load = useCallback( async () => {
    if (!true_fetched) {
      setTrueFetched(true);
    }
    if (api.user && api.user.admin && !api.working_on_sb) {
      setLoading(true);

      const res = await api.getObjects<SourceBook>({ data_type: "source_book" });
      if (res) {
        const changed = res.find(o => o.name.includes("5.1"));
        // const changed = res.find(o => o.name === "Mad Homebrews");
        if (changed instanceof SourceBook) {
          api.working_on_sb = changed;
          setWorkingOnSB(changed);
          setReloading(true);
        }
      }
      setLoading(false);
    } else {
      setReloading(true);
    }
  }, [ping, true_fetched]);

  const reload = useCallback(() => {
    // console.log("Reload");
    if (true_fetched) {
      setTrueFetched(false);
    }
  }, [true_fetched]);

  const postReload = useCallback(() => {
    console.log("Post Reload");
    setTimeout(() => {
      load();
    }, 100);
  }, [load]);

  useEffect(() => {
    api.subscription_holder.subscribe("Reload", mounted, reload);
    api.subscription_holder.subscribe("Post Reload", mounted, postReload);
  }, []);

  useEffect(() => {
    if (!auth.isLoading && (!auth.isAuthenticated || api.user)) {
      setFetched(true);
    }
  }, [auth.isLoading, auth.isAuthenticated, api.user]);
  
  useEffect(() => {
    if (!loading && !finished_loading) {
      setFinishedLoading(true);
      updateHeight();
    }
    // Update height on window resize
    window.addEventListener('resize', updateHeight);

    // Clean up the event listener
    return () => window.removeEventListener('resize', updateHeight);
  }, [loading, finished_loading, updateHeight]);

  const renderWorkingOn = () => {
    console.log(true_fetched);
    if (true_fetched) {
      // debugger;
      if (api.online) {
        if (api.user) {
          if (api.user.admin) {
            return (
              <Grid container spacing={0} direction="row">
                <Grid
                  size={{
                    xs: 9
                  }}>
                  <SelectSourceBookBox
                    name="Working On"
                    type={ source_book_type === "" ? (working_on_sb !== null ? working_on_sb.source_book_type : "") : source_book_type }
                    id={working_on_sb !== null ? working_on_sb._id : ""}
                    archives={false}
                    onChange={(type, id) => {
                      setSourceBookType(type);
                      if (api.smart_hash.source_book) {
                        const changed = (api.smart_hash.source_book as SourceBook[]).find(o => o._id === id);
                        if (changed instanceof SourceBook) {
                          api.working_on_sb = api.working_on_sb !== null && api.working_on_sb._id === changed._id ? null : changed;
                          setWorkingOnSB(api.working_on_sb);
                          setPing(!ping);
                        }
                      }
                    }}
                  />
                </Grid>
                <Grid
                  className="header clickable"
                  onClick={() => {
                    setWOObjectOpen(true);
                  }}
                  size={{
                    xs: 3
                  }}>
                  Object
                </Grid>
                <ModalOrDrawer 
                  open={wo_object_open}
                  onClose={() => {setWOObjectOpen(false)}}>
                  { renderWorkingOnObject() }
                </ModalOrDrawer>
              </Grid>
            );
          } else {
            return (
              <div style={{ float: "right" }}>
                <BugButton />
              </div>
            );
          }
        }
      } else {
        return (
          <span className="header centered">
            Currently Offline. Limited Access.
          </span>
        );
      }
    }
    return null;
  }

  const renderWorkingOnObject = () => {
    return (
      <Grid container spacing={0} direction="row">
        <Grid
          className="header centered"
          size={{
            xs: 12
          }}>
          Working on Object
        </Grid>
        <Grid
          size={{
            xs: 12
          }}>
          <SelectStringBox
            name="Setting"
            options={WORLD_SETTINGS}
            extra_options={[{ id: "", text: "None" }]}
            value={api.working_on_setting}
            onChange={(changed: WorldSetting | "") => {
              api.working_on_setting = changed;
              setPing(!ping);
            }}
          />
        </Grid>
        <Grid
          size={{
            xs: 12
          }}>
          <StringsBox
            name="Tags"
            values={api.working_on_tags}
            onChange={(changed: string[]) => {
              api.working_on_tags = changed;
              setPing(!ping);
            }}
          />
        </Grid>
        <Grid
          size={{
            xs: 12
          }}>
          <SelectStringBox
            name="Data Type"
            options={DATA_TYPE_OPTIONS}
            value={api.working_on_object.data_type}
            onChange={(changed: DataType) => {
              api.working_on_object = { data_type: changed };
              setPing(!ping);
            }}
          />
        </Grid>
        { renderWorkingOnObjectOfDataType() }
      </Grid>
    );
  }

  const renderWorkingOnObjectOfDataType = () => {
    // console.log(api.working_on_object.data_type);
    switch (api.working_on_object.data_type) {
      // case "spell":
      //   return (
      //     <>
      //       { !reloading && Object.keys(api.working_on_object).map((o, key) => {
      //         if (o !== "data_type") {
      //           return (
      //             <Grid item xs={12} key={key}>
      //               <Grid container spacing={0} direction="row">
      //                 <Grid item xs={5}>
      //                   <SelectStringBox
      //                     name="Field"
      //                     value={o}
      //                     options={SPELL_FIELDS}
      //                     onChange={(changed: string) => {
      //                       api.working_on_object[changed] = api.working_on_object[o];
      //                       delete api.working_on_object[o];
      //                       setReloading(true);
      //                     }}
      //                   />
      //                 </Grid>
      //                 <Grid item xs={5}>
      //                   {/* { o === "element" ?
      //                     <SelectStringBox
      //                       name="Value"
      //                       value={api.working_on_object[o]}
      //                       options={ELEMENTS}
      //                       onChange={(changed: string) => {
      //                         api.working_on_object[o] = changed;
      //                         setPing(!ping);
      //                       }}
      //                     />
      //                   : o === "tier" ?
      //                     <SelectStringBox
      //                       name="Value"
      //                       value={api.working_on_object[o] === "0" ? "Cantrip" : api.working_on_object[o]}
      //                       options={SPELL_TIERS}
      //                       onChange={(changed: string) => {
      //                         api.working_on_object[o] = changed === "Cantrip" ? "0" : changed;
      //                         setPing(!ping);
      //                       }}
      //                     />
      //                   :
      //                     <StringBox
      //                       name="Value"
      //                       value={api.working_on_object[o]}
      //                       onBlur={(changed: string) => {
      //                         api.working_on_object[o] = changed;
      //                         setPing(!ping);
      //                       }}
      //                     />
      //                   } */}
      //                 </Grid>
      //                 <Grid item xs={2}>
      //                   <DeleteButton
      //                     onClick={() => {
      //                       delete api.working_on_object[o];
      //                       setReloading(true);
      //                     }}
      //                   />
      //                 </Grid>
      //               </Grid>
      //             </Grid>
      //           );
      //         }
      //         return null;
      //       })}
      //       <Grid item xs={12}>
      //         <AddButton
      //           disabled={ Object.keys(api.working_on_object).includes("New Field") }
      //           onClick={() => {
      //             api.working_on_object["New Field"] = "";
      //             setReloading(true);
      //           }}
      //         />
      //       </Grid>
      //     </>
      //   );
      case "feat":
        return (
          <>
            { !reloading && Object.keys(api.working_on_object).map((o, key) => {
              if (o !== "data_type") {
                return (
                  <Grid
                    key={key}
                    size={{
                      xs: 12
                    }}>
                    <Grid container spacing={0} direction="row">
                      <Grid
                        size={{
                          xs: 5
                        }}>
                        <SelectStringBox
                          name="Field"
                          value={o}
                          options={FEAT_FIELDS}
                          onChange={(changed: string) => {
                            api.working_on_object[changed] = api.working_on_object[o];
                            delete api.working_on_object[o];
                            setReloading(true);
                          }}
                        />
                      </Grid>
                      <Grid
                        size={{
                          xs: 5
                        }}>
                        { o === "feat_tier" ?
                          <SelectStringBox
                            name="Value"
                            value={api.working_on_object[o]}
                            options={FEAT_TIERS}
                            onChange={(changed: string) => {
                              api.working_on_object[o] = changed;
                              setPing(!ping);
                            }}
                          />
                        // : o === "repeatable" ?
                        //   <CheckBox
                        //     name="Value"
                        //     value={api.working_on_object[o] === "true"}
                        //     onChange={(changed: boolean) => {
                        //       api.working_on_object[o] = changed ? "true" : "false";
                        //       setPing(!ping);
                        //     }}
                        //   />
                        :
                          <StringBox
                            name="Value"
                            value={api.working_on_object[o]}
                            onBlur={(changed: string) => {
                              api.working_on_object[o] = changed;
                              setPing(!ping);
                            }}
                          />
                        }
                      </Grid>
                      <Grid
                        size={{
                          xs: 2
                        }}>
                        <DeleteButton
                          onClick={() => {
                            delete api.working_on_object[o];
                            setReloading(true);
                          }}
                        />
                      </Grid>
                    </Grid>
                  </Grid>
                );
              }
              return null;
            })}
            <Grid
              size={{
                xs: 12
              }}>
              <AddButton
                disabled={ Object.keys(api.working_on_object).includes("New Field") }
                onClick={() => {
                  api.working_on_object["New Field"] = "";
                  setReloading(true);
                }}
              />
            </Grid>
          </>
        );
      case "deity":
        return (
          <>
            { !reloading && Object.keys(api.working_on_object).map((o, key) => {
              if (o !== "data_type") {
                return (
                  <Grid
                    key={key}
                    size={12}>
                    <Grid container spacing={0} direction="row">
                      <Grid size={5}>
                        <SelectStringBox
                          name="Field"
                          value={o}
                          options={DEITY_FIELDS}
                          onChange={(changed: string) => {
                            api.working_on_object[changed] = api.working_on_object[o];
                            delete api.working_on_object[o];
                            setReloading(true);
                          }}
                        />
                      </Grid>
                      <Grid size={5}>
                        { o === "pantheons" ?
                          <StringBox
                            name="Value"
                            value={api.working_on_object[o]}
                            onBlur={(changed: string) => {
                              api.working_on_object[o] = changed;
                              setPing(!ping);
                            }}
                          />
                        // : o === "repeatable" ?
                        //   <CheckBox
                        //     name="Value"
                        //     value={api.working_on_object[o] === "true"}
                        //     onChange={(changed: boolean) => {
                        //       api.working_on_object[o] = changed ? "true" : "false";
                        //       setPing(!ping);
                        //     }}
                        //   />
                        :
                          <StringBox
                            name="Value"
                            value={api.working_on_object[o]}
                            onBlur={(changed: string) => {
                              api.working_on_object[o] = changed;
                              setPing(!ping);
                            }}
                          />
                        }
                      </Grid>
                      <Grid size={2}>
                        <DeleteButton
                          onClick={() => {
                            delete api.working_on_object[o];
                            setReloading(true);
                          }}
                        />
                      </Grid>
                    </Grid>
                  </Grid>
                );
              }
              return null;
            })}
            <Grid size={12}>
              <AddButton
                disabled={ Object.keys(api.working_on_object).includes("New Field") }
                onClick={() => {
                  api.working_on_object["New Field"] = "";
                  setReloading(true);
                }}
              />
            </Grid>
          </>
        );
      default: 
        return (
          <>
            { !reloading && Object.keys(api.working_on_object).map((o, key) => {
              if (o !== "data_type") {
                return (
                  <Grid
                    key={key}
                    size={{
                      xs: 12
                    }}>
                    <Grid container spacing={0} direction="row">
                      <Grid
                        size={{
                          xs: 5
                        }}>
                        <StringBox
                          name="Field"
                          value={o}
                          onBlur={(changed: string) => {
                            api.working_on_object[changed] = api.working_on_object[o];
                            delete api.working_on_object[o];
                            setReloading(true);
                          }}
                        />
                      </Grid>
                      <Grid
                        size={{
                          xs: 5
                        }}>
                        <StringBox
                          name="Value"
                          value={api.working_on_object[o]}
                          onBlur={(changed: string) => {
                            api.working_on_object[o] = changed;
                            setPing(!ping);
                          }}
                        />
                      </Grid>
                      <Grid
                        size={{
                          xs: 2
                        }}>
                        <DeleteButton
                          onClick={() => {
                            delete api.working_on_object[o];
                            setReloading(true);
                          }}
                        />
                      </Grid>
                    </Grid>
                  </Grid>
                );
              }
              return null;
            })}
            <Grid
              size={{
                xs: 12
              }}>
              <AddButton
                disabled={ Object.keys(api.working_on_object).includes("New Field") }
                onClick={() => {
                  api.working_on_object["New Field"] = "";
                  setReloading(true);
                }}
              />
            </Grid>
          </>
        );
    }
  }

  // ── Sign In (redirects to Cognito Hosted UI) ──────────────────────────────
  const handleSignIn = useCallback(() => {
    localStorage.setItem("redirectRememberURL", window.location.href);

    auth.signinRedirect();
  }, [auth]);

  if (!fetched || loading) {
    return (<div className="centered"><LoadingBox /></div>);
  } else {
    return (
      <AppBar position="static" ref={heightFinder}>
        <Toolbar sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          {/* Left: Menu button */}
          <ListItem
            aria-controls="user-menu" aria-haspopup="true"
            style={{ cursor: 'pointer', width: 'auto' }}
            onClick={() => setMenuOpen(true)}>
            <MenuIcon />
            <ListItemText primary="Menu" />
          </ListItem>

          {/* Center: Working on (admin only, takes remaining space) */}
          <div style={{ flex: 1, margin: '0 16px' }}>
            { renderWorkingOn() }
          </div>

          {/* Right: Login or UserMenu */}
          { api.user === null ?
            <Grid container spacing={0} direction="row">
              <Grid size={6}>
                <ListItem
                  aria-controls="user-menu" aria-haspopup="true"
                  className="curvedButton"
                  style={{ cursor: 'pointer', width: 'auto' }}
                  onClick={handleSignIn}>
                  <PersonIcon />
                  <ListItemText primary=" Login/Register" />
                </ListItem>
              </Grid>
              <Grid size={6}>
                <ListItem
                  aria-controls="user-menu" aria-haspopup="true"
                  className="curvedButton"
                  style={{ cursor: 'pointer', width: 'auto' }}
                  onClick={toggleLogin}>
                  <PersonIcon />
                  <ListItemText primary=" Legacy Login" />
                </ListItem>
              </Grid>
            </Grid>
          :
            <UserMenu />
          }
        </Toolbar>
        <Drawer anchor="left"
          open={menuOpen}
          onClose={() => setMenuOpen(false)}>
          <div className="SidebarLabs">
            { menuOpen &&
              <Sidebar logoText="Homebrew Labs"
                onNavigate={() => setMenuOpen(false)}
              />
            }
          </div>
        </Drawer>
      </AppBar>
    );
  }
}
